import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

// HIPAA §164.312(a)(2)(iii) automatic logoff: force re-authentication after a
// period of inactivity, with a warning window so an in-progress read isn't
// cut off without notice.
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // total idle time before logout
const WARNING_BEFORE_MS = 5 * 60 * 1000; // show the warning this long before logout
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];

// Non-Hub sessions share one token in localStorage across every tab on this
// origin (see authStorage() in services/api.ts) - but each tab runs its own
// independent copy of this component with its own timers. Without this, an
// idle background tab's timer would fire logout() and clear the token out
// from under a different tab the user is actively working in. Broadcasting
// activity via localStorage (and listening for the resulting 'storage'
// event, which only ever fires in *other* tabs, never the one that wrote
// it) keeps every tab's clock in sync. Hub-embedded sessions use
// sessionStorage instead, which is already isolated per tab, so there's
// nothing to synchronize there.
const LAST_ACTIVITY_KEY = 'suite_last_activity_broadcast';
const isHubSession = () => sessionStorage.getItem('is_hub_session') === 'true';
const BROADCAST_THROTTLE_MS = 5000;

export const IdleTimeoutGuard: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(WARNING_BEFORE_MS / 1000));

  const warningOpenRef = useRef(false);
  const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastBroadcastRef = useRef(0);

  const broadcastActivity = useCallback(() => {
    if (isHubSession()) return;
    const now = Date.now();
    if (now - lastBroadcastRef.current < BROADCAST_THROTTLE_MS) return;
    lastBroadcastRef.current = now;
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    } catch {
      // Broadcasting is a nice-to-have cross-tab sync - if storage access
      // throws (e.g. some private-browsing modes), this tab's own timeout
      // still works correctly on its own.
    }
  }, []);

  useEffect(() => {
    warningOpenRef.current = warningOpen;
  }, [warningOpen]);

  const clearAllTimers = () => {
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  };

  const doLogout = useCallback(() => {
    clearAllTimers();
    setWarningOpen(false);
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const resetTimers = useCallback(() => {
    broadcastActivity();
    clearAllTimers();
    setWarningOpen(false);
    setSecondsLeft(Math.floor(WARNING_BEFORE_MS / 1000));

    warnTimerRef.current = setTimeout(() => {
      setWarningOpen(true);
      let remaining = Math.floor(WARNING_BEFORE_MS / 1000);
      setSecondsLeft(remaining);
      countdownRef.current = setInterval(() => {
        remaining -= 1;
        setSecondsLeft(Math.max(remaining, 0));
      }, 1000);
      logoutTimerRef.current = setTimeout(doLogout, WARNING_BEFORE_MS);
    }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);
  }, [doLogout, broadcastActivity]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      setWarningOpen(false);
      return;
    }

    resetTimers();

    const handleActivity = () => {
      // Ignore activity while the warning is up - only the dialog's own
      // buttons should be able to dismiss it, so a stray mouse movement
      // can't silently clear a warning the user was meant to see.
      if (warningOpenRef.current) return;
      resetTimers();
    };

    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, handleActivity, { passive: true }));

    // Cross-origin limitation: the DICOM viewer (OHIF, served from Orthanc's
    // own origin) runs in an <iframe>. Mouse/keyboard events that occur while
    // focus is inside a cross-origin frame never reach this window's
    // listeners at all - that's a browser security boundary, not something
    // fixable from the parent page. The only signal available from here is
    // whether the iframe currently holds document focus, so treat that as a
    // recurring activity ping while it does, rather than falsely timing out
    // a radiologist mid-read. This does mean a session left genuinely
    // unattended while the viewer still has focus won't auto-expire from
    // idle time alone - the general-app listeners above remain the primary
    // safeguard for every other screen (worklists, reports, admin pages).
    const viewerFocusPoll = setInterval(() => {
      if (warningOpenRef.current) return;
      const active = document.activeElement;
      if (active && active.tagName === 'IFRAME') {
        resetTimers();
      }
    }, 20000);

    // Cross-tab sync: another tab's genuine activity is strong evidence the
    // user is still present, so resync (and dismiss any warning) here too -
    // unlike same-tab mouse jitter, this is a deliberate signal from
    // elsewhere, not ambient noise, so it isn't gated on warningOpenRef.
    const handleStorageActivity = (e: StorageEvent) => {
      if (e.key === LAST_ACTIVITY_KEY && e.newValue && !isHubSession()) {
        resetTimers();
      }
    };
    window.addEventListener('storage', handleStorageActivity);

    return () => {
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, handleActivity));
      window.removeEventListener('storage', handleStorageActivity);
      clearInterval(viewerFocusPoll);
      clearAllTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Dialog open={warningOpen} onClose={resetTimers} maxWidth="xs" fullWidth>
      <DialogTitle>Still there?</DialogTitle>
      <DialogContent>
        <Typography>
          You've been inactive for a while. For security, you'll be logged out in{' '}
          <b>{secondsLeft}s</b> unless you stay logged in.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={doLogout} color="inherit">Log Out Now</Button>
        <Button onClick={resetTimers} variant="contained">Stay Logged In</Button>
      </DialogActions>
    </Dialog>
  );
};
